import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { FaAngleUp, FaAngleDown } from 'react-icons/fa';
import type { RefObject } from 'react';
import { u2000FocusNextFocusableAfterPaint } from '../../../tools/U2000FormInputUtils';
import {
  u2000IsComboBoxTypeaheadDebugEnabled,
  u2000TraceComboBoxTypeahead,
} from '../../../tools/u2000ComboBoxTypeaheadDebug';
import {
  U2000_COMBO_BOX_NONE_ITEM_KEY,
  resolveTabCommitItem,
  u2000ComboBoxHasCommittedSelection,
} from '../../../tools/u2000ComboBoxCommitUtils';
import {
  u2000MatchContainsAccentInsensitive,
  u2000SplitLabelForTypeaheadHighlight,
} from '../../../tools/u2000StringMatchUtils';

interface U2000ComboBoxBaseProps<T extends Record<string, unknown> = Record<string, unknown>> {
  keyField?: keyof T;
  descField?: keyof T;
  data: T[];
  value?: T | null;
  placeHolder?: string;
  onChange?: (key: string | null) => void;
  onChangeObject?: (field: T | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  /**
   * Hauteur maximale du dropdown en pixels (défaut: 300)
   */
  maxHeight?: number;
  /**
   * Ref to the button element for external focus control
   */
  inputRef?: RefObject<HTMLButtonElement | null>;
  /**
   * Libellé pour l'item "aucun item sélectionné" (défaut: "Aucune sélection")
   */
  noneItemLabel?: string;
  /** Intégré dans un conteneur parent (sans bordure propre — ex. tags Parmi critère recherche). */
  embedded?: boolean;
  /** Grille compacte U3129 (h-8) — aligné date / autocomplete / inputs. */
  compactGrid?: boolean;
}

/** Reset du buffer uniquement menu fermé — voir scheduleTypeaheadReset */
const TYPEAHEAD_RESET_MS = 1000;

function isTypeaheadCharacter(key: string): boolean {
  return /^[\p{L}\p{N}]$/u.test(key);
}

function U2000ComboBoxOptionLabel({ label, typeaheadQuery }: { label: string; typeaheadQuery: string }) {
  const segments = React.useMemo(
    () => u2000SplitLabelForTypeaheadHighlight(label, typeaheadQuery),
    [label, typeaheadQuery],
  );

  if (!typeaheadQuery.trim()) {
    return <>{label}</>;
  }

  return (
    <>
      {segments.map((segment, index) =>
        segment.highlighted ? (
          <mark
            key={index}
            className="mx-px rounded-sm px-0.5 font-bold text-gray-950 bg-yellow-300 shadow-xs ring-2 ring-inset ring-amber-500 group-aria-selected:bg-white group-aria-selected:text-blue-900 group-aria-selected:ring-blue-600"
          >
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </>
  );
}

/**
 * Composant ComboBox de base avec support pour highlight automatique et option "aucun item sélectionné"
 *
 * @see MA-12 - U2000ComboBox - Amélioration de la sélection et ajout option "aucun item"
 * @see CODE_REVIEW_MA-12.md pour la revue de code complète
 * @see BL-0029 - Typeahead clavier (contains, sans accents)
 * @see BL-0057 - Tab valide l’option surlignée (flèches / souris)
 * @see BL-0107 - Entrée = Tab menu fermé (valeur sélectionnée) ; Entrée commit menu ouvert
 *
 * Fonctionnalités :
 * - Highlight automatique de la valeur sélectionnée à l'ouverture du popup
 * - Item "aucun item sélectionné" injecté au début de la liste
 * - Support de null dans les handlers onChange et onChangeObject
 * - Typeahead : filtre contains insensible casse/accents sur le libellé affiché
 */
function U2000ComboBoxBase<T extends Record<string, unknown> = Record<string, unknown>>({
  keyField = 'key' as keyof T,
  descField = 'description' as keyof T,
  data,
  value,
  placeHolder = '',
  onChange,
  onChangeObject,
  onFocus,
  onBlur,
  disabled = false,
  maxHeight = 300,
  inputRef,
  noneItemLabel = 'Aucune sélection',
  embedded = false,
  compactGrid = false,
}: U2000ComboBoxBaseProps<T>) {
  const comboDebugId = React.useId();
  const [isOpen, setIsOpen] = React.useState(false);
  const [cmbValue, setCmbValue] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [typeaheadQuery, setTypeaheadQuery] = React.useState('');
  /** Position du dropdown en fixed (évite le clipping dans tableaux avec overflow) */
  const [dropdownRect, setDropdownRect] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const cmbParentRef = useRef<HTMLDivElement>(null);
  const internalButtonRef = useRef<HTMLButtonElement | null>(null);
  const cmbChildRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const blurTimeoutRef = useRef<number | null>(null);
  const typeaheadResetTimerRef = useRef<number | null>(null);
  const isOpenRef = useRef(false);
  /** Dernière valeur (clé + affichage) synchronisée depuis value/data — évite setState si inchangé (boucle infinie) */
  const lastSyncedRef = useRef<{ key: string | null; display: string }>({ key: null, display: '' });
  const keyboardStateRef = useRef({
    isOpen,
    typeaheadQuery,
    highlightedIndex,
    displayItems: [] as T[],
    keyField,
    descField,
  });
  const commitDedupeRef = useRef(0);
  const tryCommitOnNavigationKeyRef = useRef<
    (e: React.KeyboardEvent<HTMLButtonElement>, source: 'button' | 'capture') => boolean
  >(() => false);

  const traceTypeahead = React.useCallback(
    (event: string, detail: Record<string, unknown>) => {
      u2000TraceComboBoxTypeahead(comboDebugId, event, detail);
    },
    [comboDebugId],
  );

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!u2000IsComboBoxTypeaheadDebugEnabled()) {
      return;
    }
    u2000TraceComboBoxTypeahead(comboDebugId, 'mount', {
      hint: "Traces actives en DEV. Désactiver : localStorage.setItem('u2000ComboBoxTypeaheadDebug','0')",
      dataCount: data.length,
      placeHolder,
    });
  }, [comboDebugId, data.length, placeHolder]);

  const updateDropdownRect = React.useCallback(() => {
    if (cmbParentRef.current) {
      const rect = cmbParentRef.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom, left: rect.left, width: rect.width });
    } else {
      setDropdownRect(null);
    }
  }, []);

  const noneItem = React.useMemo(
    () =>
      ({
        [keyField]: U2000_COMBO_BOX_NONE_ITEM_KEY,
        [descField]: noneItemLabel,
      }) as T,
    [keyField, descField, noneItemLabel],
  );

  const dataWithNoneItem = React.useMemo(() => [noneItem, ...data], [noneItem, data]);

  const displayItems = React.useMemo(() => {
    const query = typeaheadQuery.trim();
    if (!query) {
      return dataWithNoneItem;
    }
    const filtered = data.filter((item) =>
      u2000MatchContainsAccentInsensitive(String(item[descField]), query),
    );
    const items = [noneItem, ...filtered];
    traceTypeahead('displayItems', {
      query,
      total: dataWithNoneItem.length,
      shown: items.length,
      labels: items.slice(0, 8).map((item) => String(item[descField])),
    });
    return items;
  }, [typeaheadQuery, dataWithNoneItem, data, noneItem, descField, traceTypeahead]);

  keyboardStateRef.current = {
    isOpen,
    typeaheadQuery,
    highlightedIndex,
    displayItems,
    keyField,
    descField,
  };

  useEffect(() => {
    keyboardStateRef.current = {
      isOpen,
      typeaheadQuery,
      highlightedIndex,
      displayItems,
      keyField,
      descField,
    };
  }, [isOpen, typeaheadQuery, highlightedIndex, displayItems, keyField, descField]);

  const clearTypeaheadResetTimer = React.useCallback(() => {
    if (typeaheadResetTimerRef.current !== null) {
      window.clearTimeout(typeaheadResetTimerRef.current);
      typeaheadResetTimerRef.current = null;
    }
  }, []);

  const clearTypeaheadQuery = React.useCallback((reason: string) => {
    traceTypeahead('clearQuery', { reason, previousQuery: typeaheadQuery });
    clearTypeaheadResetTimer();
    setTypeaheadQuery('');
  }, [clearTypeaheadResetTimer, typeaheadQuery]);

  const scheduleTypeaheadReset = React.useCallback(() => {
    clearTypeaheadResetTimer();
    typeaheadResetTimerRef.current = window.setTimeout(() => {
      typeaheadResetTimerRef.current = null;
      if (isOpenRef.current) {
        traceTypeahead('inactivityResetSkipped', {
          reason: 'menu still open',
          ms: TYPEAHEAD_RESET_MS,
        });
        return;
      }
      traceTypeahead('inactivityReset', { ms: TYPEAHEAD_RESET_MS });
      setTypeaheadQuery('');
    }, TYPEAHEAD_RESET_MS);
    traceTypeahead('inactivityResetScheduled', { ms: TYPEAHEAD_RESET_MS, isOpen: isOpenRef.current });
  }, [clearTypeaheadResetTimer]);

  const closeDropdown = React.useCallback(() => {
    traceTypeahead('closeDropdown', {});
    setIsOpen(false);
    setHighlightedIndex(-1);
    clearTypeaheadQuery('closeDropdown');
  }, [clearTypeaheadQuery]);

  const openDropdown = React.useCallback(() => {
    updateDropdownRect();
    setIsOpen(true);
    if (onFocus) {
      onFocus();
    }
  }, [updateDropdownRect, onFocus]);

  const appendTypeahead = React.useCallback(
    (char: string) => {
      setTypeaheadQuery((prev) => {
        const next = prev + char;
        traceTypeahead('append', { char, prev, next, isOpen: isOpenRef.current });
        scheduleTypeaheadReset();
        return next;
      });
    },
    [scheduleTypeaheadReset, traceTypeahead],
  );

  const handleTypeaheadBackspace = React.useCallback(() => {
    setTypeaheadQuery((prev) => {
      const next = prev.slice(0, -1);
      if (next) {
        scheduleTypeaheadReset();
      } else {
        clearTypeaheadResetTimer();
      }
      return next;
    });
  }, [scheduleTypeaheadReset, clearTypeaheadResetTimer]);

  const shouldAppendSpaceToTypeahead = (key: string, query: string) => key === ' ' && query.length > 0;

  const handleTypeaheadKey = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isTypeaheadCharacter(e.key) || shouldAppendSpaceToTypeahead(e.key, typeaheadQuery)) {
        e.preventDefault();
        if (!isOpen) {
          openDropdown();
        }
        appendTypeahead(e.key);
        return true;
      }
      return false;
    },
    [typeaheadQuery, isOpen, openDropdown, appendTypeahead],
  );

  const assignButtonRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      internalButtonRef.current = node;
      if (inputRef) {
        (inputRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      }
    },
    [inputRef],
  );

  const resolveFocusOrigin = React.useCallback(
    (fallback: HTMLElement): HTMLElement => {
      return inputRef?.current ?? internalButtonRef.current ?? fallback;
    },
    [inputRef],
  );

  function handleClick() {
    if (disabled) return;
    if (!isOpen) {
      if (onFocus) onFocus();
      updateDropdownRect();
      setIsOpen(true);
      // Le useEffect gérera le highlight de la valeur sélectionnée
    } else {
      closeDropdown();
    }
  }

  function tryCommitOnNavigationKey(e: React.KeyboardEvent<HTMLButtonElement>, source: 'button' | 'capture'): boolean {
    const state = keyboardStateRef.current;
    if (!state.isOpen) {
      traceTypeahead('commitSkip', { source, key: e.key, reason: 'menu closed' });
      return false;
    }
    if (e.key !== 'Tab' && e.key !== 'Enter') {
      return false;
    }

    const dataMatches = state.displayItems.filter(
      (item) => String(item[state.keyField]) !== U2000_COMBO_BOX_NONE_ITEM_KEY,
    );
    const commitItem = resolveTabCommitItem(
      state.displayItems,
      state.keyField,
      state.typeaheadQuery,
      state.highlightedIndex,
    );

    traceTypeahead('commitKey', {
      source,
      key: e.key,
      typeaheadQuery: state.typeaheadQuery,
      highlightedIndex: state.highlightedIndex,
      displayCount: state.displayItems.length,
      dataMatchCount: dataMatches.length,
      dataMatchLabels: dataMatches.map((item) => String(item[state.descField])),
      highlightedLabel:
        state.highlightedIndex >= 0 && state.highlightedIndex < state.displayItems.length
          ? String(state.displayItems[state.highlightedIndex][state.descField])
          : null,
      commitKey: commitItem ? String(commitItem[state.keyField]) : null,
      commitLabel: commitItem ? String(commitItem[state.descField]) : null,
    });

    const now = Date.now();
    if (commitItem) {
      if (now - commitDedupeRef.current < 80) {
        traceTypeahead('commitDedupe', { source, key: e.key });
        return true;
      }
      commitDedupeRef.current = now;
      e.preventDefault();
      selectTypeaheadItemAndFocusNext(
        commitItem,
        resolveFocusOrigin(e.currentTarget),
        state.typeaheadQuery.trim() ? 'nav-typeahead-commit' : 'nav-highlight-commit',
      );
      return true;
    }

    if (now - commitDedupeRef.current < 80) {
      traceTypeahead('commitDedupe', { source, key: e.key });
      return true;
    }
    commitDedupeRef.current = now;

    traceTypeahead('navDismiss', {
      source,
      typeaheadQuery: state.typeaheadQuery,
      highlightedIndex: state.highlightedIndex,
      reason: 'no commit item — fermeture + focus suivant',
    });
    e.preventDefault();
    setIsOpen(false);
    setHighlightedIndex(-1);
    clearTypeaheadQuery('nav-dismiss');
    u2000FocusNextFocusableAfterPaint(resolveFocusOrigin(e.currentTarget), 24, comboDebugId);
    return true;
  }

  tryCommitOnNavigationKeyRef.current = tryCommitOnNavigationKey;

  // Gestion de la navigation clavier
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.nativeEvent.isComposing) return;

    traceTypeahead('keyDown', {
      key: e.key,
      isOpen,
      typeaheadQuery,
      highlightedIndex,
    });

    if (e.key === 'Backspace' && (isOpen || typeaheadQuery.length > 0)) {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
      }
      handleTypeaheadBackspace();
      return;
    }

    if (handleTypeaheadKey(e)) {
      return;
    }

    if (!isOpen) {
      if (
        e.key === 'Enter' &&
        u2000ComboBoxHasCommittedSelection(value, keyField, cmbValue)
      ) {
        e.preventDefault();
        traceTypeahead('enterFocusNext', { reason: 'closed-menu-committed-value' });
        u2000FocusNextFocusableAfterPaint(resolveFocusOrigin(e.currentTarget), 24, comboDebugId);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDropdown();
        if (value) {
          const selectedIndex = dataWithNoneItem.findIndex((item) => String(item[keyField]) === String(value[keyField]));
          setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        } else {
          setHighlightedIndex(0);
        }
      }
      return;
    }

    if (tryCommitOnNavigationKey(e, 'button')) {
      return;
    }

    // Navigation dans le dropdown ouvert
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < displayItems.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case ' ':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < displayItems.length) {
          const item = displayItems[highlightedIndex];
          handleChangeValue(String(item[keyField]), String(item[descField]), item);
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        break;
    }
  };

  function selectTypeaheadItemAndFocusNext(
    item: T,
    focusOrigin: HTMLElement,
    reason: string,
  ) {
    traceTypeahead('typeaheadCommit', {
      reason,
      key: String(item[keyField]),
      label: String(item[descField]),
      hasOnChangeObject: Boolean(onChangeObject),
    });
    handleChangeValue(String(item[keyField]), String(item[descField]), item);
    u2000FocusNextFocusableAfterPaint(focusOrigin, 24, comboDebugId);
  }

  function handleChangeValue(key: string, description: string, sourceItem?: T) {
    clearTypeaheadQuery('select');
    setIsOpen(false);

    // Si c'est l'item "aucun item sélectionné", passer null aux handlers
    if (key === U2000_COMBO_BOX_NONE_ITEM_KEY) {
      setCmbValue('');
      if (onChange) onChange(null);
      if (onChangeObject) onChangeObject(null);
      traceTypeahead('changeValue', { key, cleared: true });
      return;
    }

    setCmbValue(description);
    if (onChange) onChange(key);
    if (onChangeObject) {
      const resolved = sourceItem ?? data.find((item) => String(item[keyField]) === key);
      if (resolved) {
        onChangeObject(resolved);
        traceTypeahead('onChangeObject', { key, label: description });
      } else {
        traceTypeahead('onChangeObjectMiss', { key, description });
      }
    }
    traceTypeahead('changeValue', { key, description });
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        cmbChildRef.current &&
        !cmbChildRef.current.contains(event.target as Node) &&
        cmbParentRef.current &&
        !cmbParentRef.current.contains(event.target as Node) &&
        !cmbChildRef.current.classList.contains('hiddend')
      ) {
        closeDropdown();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeDropdown]);

  useEffect(() => {
    return () => {
      clearTypeaheadResetTimer();
    };
  }, [clearTypeaheadResetTimer]);

  // Sync affichage depuis value/data. N'appeler setCmbValue que si la clé ou le libellé a vraiment changé
  useEffect(() => {
    const currentKey = value != null ? String(value[keyField]) : null;
    let display = '';
    if (value) {
      const item = data.find((item) => String(item[keyField]) === currentKey);
      display = item ? String(item[descField]) : "";
    }
    if (lastSyncedRef.current.key === currentKey && lastSyncedRef.current.display === display) {
      return;
    }
    lastSyncedRef.current = { key: currentKey, display };
    setCmbValue(display);
  }, [value, data, keyField, descField]);

  // Réinitialiser itemRefs quand displayItems change
  useEffect(() => {
    itemRefs.current = new Array(displayItems.length).fill(null);
  }, [displayItems.length]);

  // Typeahead : ne pas laisser « Aucune sélection » surlignée quand des résultats existent
  useLayoutEffect(() => {
    if (!isOpen || !typeaheadQuery.trim()) {
      return;
    }
    const dataRows = displayItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => String(item[keyField]) !== U2000_COMBO_BOX_NONE_ITEM_KEY);

    if (dataRows.length === 0) {
      setHighlightedIndex(0);
      traceTypeahead('highlight', { mode: 'none-only', index: 0 });
      return;
    }

    const preferredIndex = dataRows.length === 1 ? dataRows[0].index : dataRows[0].index;
    setHighlightedIndex((prev) => {
      const current = displayItems[prev];
      const currentIsDataRow =
        current != null && String(current[keyField]) !== U2000_COMBO_BOX_NONE_ITEM_KEY;
      const prevStillVisible =
        currentIsDataRow && dataRows.some((row) => row.index === prev);
      const next = prevStillVisible ? prev : preferredIndex;
      if (next !== prev) {
        traceTypeahead('highlight', {
          mode: dataRows.length === 1 ? 'single' : 'first-data',
          index: next,
          label: String(displayItems[next][descField]),
          dataMatchCount: dataRows.length,
        });
      }
      return next;
    });
  }, [isOpen, typeaheadQuery, displayItems, keyField, descField, traceTypeahead]);

  // Réinitialiser l'index surligné à l'ouverture (sauf pendant un typeahead actif)
  useEffect(() => {
    if (!isOpen) return;
    if (typeaheadQuery.trim()) {
      return;
    }
    let nextIndex: number;
    if (value) {
      const selectedIndex = displayItems.findIndex((item) => String(item[keyField]) === String(value[keyField]));
      nextIndex = selectedIndex >= 0 ? selectedIndex : 0;
    } else {
      nextIndex = 0;
    }
    setHighlightedIndex((prev) => (prev === nextIndex ? prev : nextIndex));
  }, [isOpen, value, displayItems, keyField, typeaheadQuery]);

  // Scroll vers l'élément surligné
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedIndex, isOpen]);

  // Position fixed du dropdown à l'ouverture (évite clipping dans overflow)
  useEffect(() => {
    if (isOpen) {
      updateDropdownRect();
      const onScrollOrResize = () => updateDropdownRect();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    } else {
      setDropdownRect(null);
    }
  }, [isOpen, updateDropdownRect]);

  const typeaheadActive = typeaheadQuery.length > 0;
  /** Menu ouvert : masquer la valeur sélectionnée et montrer le buffer de frappe */
  const buttonDisplayText = isOpen ? typeaheadQuery : cmbValue;
  const buttonShowsPlaceholder = !isOpen && !cmbValue && !disabled;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onDocKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' && event.key !== 'Enter') {
        return;
      }
      const buttonEl = internalButtonRef.current ?? inputRef?.current ?? cmbParentRef.current?.querySelector('button');
      if (!buttonEl || document.activeElement !== buttonEl) {
        return;
      }
      traceTypeahead('captureKey', { key: event.key, activeElement: 'button' });
      tryCommitOnNavigationKeyRef.current(
        event as unknown as React.KeyboardEvent<HTMLButtonElement>,
        'capture',
      );
    };
    document.addEventListener('keydown', onDocKeyDown, true);
    return () => document.removeEventListener('keydown', onDocKeyDown, true);
  }, [isOpen, traceTypeahead, inputRef]);

  return (
    <div className="relative w-full" ref={cmbParentRef}>
      {/* Field Name */}
      <button
        ref={assignButtonRef}
        type="button"
        className={`flex w-full items-center justify-between text-sm transition-all duration-200 focus:outline-hidden disabled:cursor-not-allowed ${
          embedded
            ? `h-auto min-h-0 border-0 bg-transparent px-0 py-0 shadow-none focus:ring-0 disabled:bg-transparent disabled:text-gray-500 ${
                isOpen ? 'text-blue-700' : 'text-gray-900'
              }`
            : compactGrid
              ? `h-8 min-h-8 max-h-8 rounded-lg border bg-surface px-2 py-0.5 focus:ring-2 disabled:border-gray-200 disabled:bg-muted disabled:text-gray-500 ${
                  isOpen ? 'border-blue-500 text-gray-900 ring-blue-500/20' : 'border-gray-300 text-gray-900 hover:border-gray-400'
                }`
              : `h-[46px] rounded-lg border bg-surface px-4 py-3 focus:ring-2 disabled:border-gray-200 disabled:bg-muted disabled:text-gray-500 ${
                  isOpen ? 'border-blue-500 text-gray-900 ring-blue-500/20' : 'border-gray-300 text-gray-900 hover:border-gray-400'
                }`
        }`}
        data-u2000-skip-grid-compact={compactGrid ? '' : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          if (blurTimeoutRef.current !== null) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
          }
          const relatedTarget = e.relatedTarget as Node;
          const isClickingDropdown =
            cmbChildRef.current?.contains(relatedTarget) || cmbParentRef.current?.contains(relatedTarget);

          if (!isClickingDropdown) {
            blurTimeoutRef.current = setTimeout(() => {
              if (!isOpen && onBlur) {
                onBlur();
              }
            }, 150);
          }
        }}
        disabled={disabled}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        title={isOpen && typeaheadActive ? typeaheadQuery : undefined}
      >
        <p
          className={`flex-1 text-left mr-3 truncate ${
            buttonShowsPlaceholder ? 'text-gray-400' : isOpen ? 'text-gray-900 tracking-wide' : ''
          }`}
        >
          {buttonShowsPlaceholder ? placeHolder : buttonDisplayText}
        </p>
        <span className={`shrink-0 transition-transform duration-200 disabled:text-gray-400 text-gray-500 ${compactGrid ? "text-xs" : ""}`}>
          {isOpen ? <FaAngleUp /> : <FaAngleDown />}
        </span>
      </button>
      {typeaheadActive ? (
        <span className="sr-only" aria-live="polite">
          {typeaheadQuery}
        </span>
      ) : null}

      {/* Dropdown Values — position:fixed quand rect dispo (évite clipping dans overflow), sinon absolute */}
      <div
        className={`${!isOpen && 'hidden'} overflow-y-auto rounded-lg border border-gray-200 bg-surface shadow-xl animate-fade-in ${dropdownRect ? '' : `absolute ${compactGrid ? 'top-[34px]' : 'top-[49px]'} w-full`}`}
        style={
          dropdownRect
            ? {
                position: 'fixed',
                top: dropdownRect.top,
                left: dropdownRect.left,
                width: dropdownRect.width,
                zIndex: 9999,
                maxHeight: `${maxHeight}px`,
              }
            : { zIndex: 9999, maxHeight: `${maxHeight}px` }
        }
        ref={cmbChildRef}
        role="listbox"
      >
        {displayItems.length > 0 &&
          displayItems.map((item, index) => (
            <div
              key={String(item[keyField])}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              className={`group cursor-pointer px-4 py-3 text-sm text-gray-900 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg ${
                highlightedIndex === index ? 'bg-blue-100 border-blue-300' : 'hover:bg-muted'
              }`}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => handleChangeValue(String(item[keyField]), String(item[descField]), item)}
              role="option"
              aria-selected={highlightedIndex === index}
            >
              <p className="truncate leading-tight">
                <U2000ComboBoxOptionLabel label={String(item[descField])} typeaheadQuery={typeaheadQuery} />
              </p>
            </div>
          ))}
      </div>
    </div>
  );
}

export default U2000ComboBoxBase;
